import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, FileSpreadsheet, RefreshCw, Upload } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  QueryErrorState,
  SearchInput,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system/permissions'
import { odooUrl } from '@/lib/utils/odoo-url'
import { fillInspecWorkbook, ownWorkbookUrl, usePos } from '../api/queries'
import type { InspecPo, Release } from '../api/types'
import { PoDrawer } from '../components/PoDrawer'
import { UploadPosDrawer } from '../components/UploadPosDrawer'
import { CALL_WINDOW_DAYS, daysUntil, fmtDate, fmtKg, fmtLb } from '../utils/format'

interface Row {
  po: InspecPo
  release: Release
}

const EDIT_ROLES = ['admin', 'operations', 'operations_logistics', 'purchasing'] as const

function callState(row: Row): 'done' | 'due' | 'later' | 'shipped' {
  const { release } = row
  if (release.qty_delivered_kg >= release.qty_kg) return 'shipped'
  if (release.pre_ship_call_at) return 'done'
  const days = daysUntil(release.eta)
  return days != null && days <= CALL_WINDOW_DAYS ? 'due' : 'later'
}

export function InspecZ1Page() {
  const canEdit = useHasAnyPermission(EDIT_ROLES)
  const [includeClosed, setIncludeClosed] = useState(false)
  const pos = usePos(includeClosed)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [openPo, setOpenPo] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [filling, setFilling] = useState(false)
  const sheetInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim().toLowerCase()), 200)
    return () => clearTimeout(id)
  }, [search])

  const items = useMemo(() => pos.data?.items ?? [], [pos.data])
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = []
    for (const po of items) {
      const hay = [po.po_number, po.lot_name, po.odoo_so_name].join(' ').toLowerCase()
      if (debounced && !hay.includes(debounced)) continue
      for (const release of po.releases) out.push({ po, release })
    }
    return out.sort((a, b) => (a.release.eta ?? '9999').localeCompare(b.release.eta ?? '9999'))
  }, [items, debounced])

  const metrics = useMemo(() => {
    const open = items.filter((p) => p.status !== 'shipped' && p.status !== 'cancelled')
    const all: Row[] = open.flatMap((po) => po.releases.map((release) => ({ po, release })))
    const due30 = all
      .filter((r) => {
        const d = daysUntil(r.release.eta)
        return d != null && d <= 30 && r.release.qty_delivered_kg < r.release.qty_kg
      })
      .reduce((s, r) => s + (r.release.qty_kg - r.release.qty_delivered_kg), 0)
    return {
      open: open.length,
      awaiting: open.filter((p) => !p.lot_name).length,
      assigned: open.filter((p) => p.lot_name).length,
      due30,
      callsDue: all.filter((r) => callState(r) === 'due').length,
      problems: open.filter((p) => p.issues.length > 0).length,
    }
  }, [items])

  const selected = items.find((p) => p.po_number === openPo) ?? null

  const columns = useMemo<ColumnDef<Row, unknown>[]>(
    () => [
      { id: 'eta', header: 'ETA', cell: (c) => fmtDate(c.row.original.release.eta) },
      {
        id: 'po',
        header: 'PO',
        cell: (c) => {
          const { po } = c.row.original
          return (
            <span className="inline-flex items-center gap-1">
              <span className="text-role-data-primary">{po.po_number}</span>
              {po.revision > 1 ? (
                <span className="text-xs text-tertiary">rev {po.revision}</span>
              ) : null}
              {po.tentative ? (
                <Chip size="xs" variant="warning">
                  Subject to change
                </Chip>
              ) : null}
              {po.cancelled ? (
                <Chip size="xs" variant="neutral">
                  Cancelled
                </Chip>
              ) : null}
            </span>
          )
        },
      },
      {
        id: 'shipment',
        header: 'Shipment',
        cell: (c) => `${c.row.original.release.seq} of ${c.row.original.po.releases.length}`,
      },
      {
        id: 'qty',
        header: 'Qty',
        cell: (c) => (
          <span>
            {fmtKg(c.row.original.release.qty_kg)}
            <span className="ml-1 text-xs text-tertiary">
              {fmtLb(c.row.original.release.qty_lb)}
            </span>
          </span>
        ),
      },
      {
        id: 'lot',
        header: 'Lot',
        cell: (c) =>
          c.row.original.po.lot_name ? (
            <span className="text-role-data-primary">{c.row.original.po.lot_name}</span>
          ) : (
            <span className="text-tertiary">Awaiting lot from Uviva</span>
          ),
      },
      {
        id: 'so',
        header: 'SPI order',
        cell: (c) => {
          const { po } = c.row.original
          if (!po.odoo_so_id)
            return (
              <Chip size="xs" variant="danger">
                Not in Odoo
              </Chip>
            )
          return (
            <a
              href={odooUrl('sale.order', po.odoo_so_id)}
              target="_blank"
              rel="noreferrer"
              className="text-link hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {po.odoo_so_name}
              {po.odoo_so_state === 'draft' ? ' (draft)' : ''}
            </a>
          )
        },
      },
      {
        id: 'delivered',
        header: 'Delivered',
        cell: (c) => {
          const r = c.row.original.release
          return r.qty_delivered_kg > 0 ? fmtKg(r.qty_delivered_kg) : '-'
        },
      },
      {
        id: 'call',
        header: 'Pre-ship call',
        cell: (c) => {
          const state = callState(c.row.original)
          if (state === 'done')
            return (
              <Chip size="xs" variant="success">
                Called
              </Chip>
            )
          if (state === 'due')
            return (
              <Chip size="xs" variant="danger">
                Call due
              </Chip>
            )
          if (state === 'shipped')
            return (
              <Chip size="xs" variant="neutral">
                Shipped
              </Chip>
            )
          return <span className="text-tertiary">Later</span>
        },
      },
      {
        id: 'issues',
        header: 'Problems',
        cell: (c) => {
          const n = c.row.original.po.issues.length
          return n ? (
            <Chip
              size="xs"
              variant="danger"
              title={c.row.original.po.issues.map((i) => i.detail).join('\n')}
            >
              {n}
            </Chip>
          ) : null
        },
      },
    ],
    [],
  )

  const onSheet = async (file: File | undefined) => {
    if (!file) return
    setFilling(true)
    try {
      const report = await fillInspecWorkbook(file)
      const parts = [`${report.filled.length} PO${report.filled.length === 1 ? '' : 's'} filled`]
      if (report.missingLot.length) parts.push(`no lot yet: ${report.missingLot.join(', ')}`)
      if (report.notInSheet.length) parts.push(`not in the sheet: ${report.notInSheet.join(', ')}`)
      toast.success(parts.join('; '))
    } catch (e) {
      toast.danger(e instanceof Error ? e.message : 'Could not fill the spreadsheet')
    } finally {
      setFilling(false)
    }
  }

  return (
    <div data-density="compact" className="flex h-full flex-col">
      <PageHeaderBand
        title="InSpec Z1"
        description="One dedicated Eclipse Z1 lot per InSpec PO. The lot comes from the Uviva shipment; Odoo holds it for that PO's deliveries."
      >
        <MetricStrip variant="inline" className="ml-auto">
          <MetricCard variant="inline" label="Open POs" value={metrics.open.toLocaleString()} />
          <MetricCard
            variant="inline"
            label="Awaiting lot"
            value={metrics.awaiting.toLocaleString()}
            valueTone={metrics.awaiting ? 'warning' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Lot assigned"
            value={metrics.assigned.toLocaleString()}
          />
          <MetricCard variant="inline" label="Due in 30 days" value={fmtKg(metrics.due30)} />
          <MetricCard
            variant="inline"
            label="Calls due"
            value={metrics.callsDue.toLocaleString()}
            valueTone={metrics.callsDue ? 'danger' : 'default'}
          />
          <MetricCard
            variant="inline"
            label="Problems"
            value={metrics.problems.toLocaleString()}
            valueTone={metrics.problems ? 'danger' : 'default'}
          />
        </MetricStrip>
      </PageHeaderBand>

      <div className="flex min-w-0 flex-1 flex-col">
        <Toolbar>
          <ToolbarLeft>
            {canEdit ? (
              <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>
                <Upload size={14} /> Upload POs
              </Button>
            ) : null}
            <input
              ref={sheetInput}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              data-testid="inspec-sheet-input"
              onChange={(e) => {
                void onSheet(e.target.files?.[0])
                e.target.value = ''
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              loading={filling}
              onClick={() => sheetInput.current?.click()}
            >
              <FileSpreadsheet size={14} /> Fill InSpec's spreadsheet
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(ownWorkbookUrl(), '_blank')}
            >
              <Download size={14} /> Lot sheet
            </Button>
          </ToolbarLeft>
          <ToolbarRight>
            <Checkbox
              label="Show shipped and cancelled"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
            />
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Refresh"
              loading={pos.isFetching && !pos.isLoading}
              onClick={() => void pos.refetch()}
            >
              <RefreshCw size={14} />
            </Button>
          </ToolbarRight>
        </Toolbar>
        <Toolbar>
          <SearchInput
            containerClassName="flex-1 max-w-none"
            placeholder="Search PO, lot, SPI order…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search"
          />
        </Toolbar>
        <Toolbar>
          <ToolbarRight>
            <ToolbarCount>
              {rows.length} shipment{rows.length === 1 ? '' : 's'} across {items.length} PO
              {items.length === 1 ? '' : 's'}
            </ToolbarCount>
          </ToolbarRight>
        </Toolbar>
        <div className="flex-1 min-h-0 overflow-auto bg-canvas p-3">
          {pos.isError ? (
            <QueryErrorState
              error={pos.error}
              onRetry={() => void pos.refetch()}
              subject="InSpec POs"
            />
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              loading={pos.isLoading}
              maxHeight="100%"
              moduleId="inspec-z1"
              getRowId={(r) => `${r.po.po_number}-${r.release.seq}`}
              onRowClick={(r) => setOpenPo(r.po.po_number)}
              emptyState={
                <EmptyState
                  variant={debounced ? 'no-match' : 'no-data'}
                  title={debounced ? 'No match' : 'No InSpec POs yet'}
                  description={
                    debounced ? 'Try another PO or lot number.' : 'Upload InSpec PO PDFs to start.'
                  }
                />
              }
            />
          )}
        </div>
      </div>

      <UploadPosDrawer open={uploadOpen} onOpenChange={setUploadOpen} />
      <PoDrawer po={selected} canEdit={canEdit} onClose={() => setOpenPo(null)} />
    </div>
  )
}
